import { Col, Divider, Form, Input, Row, Select, notification } from 'antd';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useUpdateSellerMutation } from '../../../app/api/userApiSlice';
import {
  getSellerCategories,
  getVolumeOfPotential,
  getYardTypes,
} from '../../../app/store/commonSlice';
import Seller from '../../../core/models/Seller';

interface IProps {
  seller: Seller | null;
  onSuccessfulSubmit: () => void;
  drawerVisible: boolean;
}

const EditSellerForm = ({ seller, onSuccessfulSubmit, drawerVisible }: IProps): JSX.Element => {
  const [updateSeller, { isError: isUpdateSellerError }] = useUpdateSellerMutation();

  const sellerCategories = useSelector(getSellerCategories);
  const yardTypes = useSelector(getYardTypes);
  const volumeOfPotential = useSelector(getVolumeOfPotential);

  const [form] = Form.useForm();

  const handleUpdateSeller = (values: any): void => {
    let updatedSeller: Seller;

    if (seller) {
      updatedSeller = { ...seller };
      Object.assign(updatedSeller, values);
    } else {
      updatedSeller = values;
    }

    updateSeller(updatedSeller)
      .unwrap()
      .then((response) => {
        notification.success({
          message: 'Update Seller',
          description: 'Seller updated successfully.',
        });
        onSuccessfulSubmit();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    if (!drawerVisible) {
      form.resetFields();
    }
  }, [drawerVisible]);

  return (
    <Form
      id='updateSellerForm'
      form={form}
      layout='vertical'
      initialValues={{
        ...seller,
        sellerCategoryId: seller?.sellerCategory?.id,
        yardTypeId: seller?.yardType?.id,
        volumeOfPotentialId: seller?.volumeOfPotential?.id,
      }}
      onFinish={handleUpdateSeller}
    >
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name='sellerCategoryId'
            label='Seller Category'
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select>
              {sellerCategories?.map((category) => (
                <Select.Option key={category.id} value={category.id} initialValues={true}>
                  {category.category}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name='personInCharge'
            label='Name'
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder='Name' />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name='companyName' label='Company Name'>
            <Input placeholder='Company Name' />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='representativePersonName' label='Representative Person Name'>
            <Input placeholder='Representative Person Name' />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name='phoneNumber'
            label='Phone Number'
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder='Phone Number' />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name='addressLine1'
            label='Address 1'
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder='Address Line 1' />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name='addressLine2' label='Address 2'>
            <Input placeholder='Address Line 2' />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='city' label='City' rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder='City' />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name='postalCode'
            label='Postal Code'
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder='Postal Code' />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='yardTypeId' label='Yard Preference'>
            <Select>
              {yardTypes?.map((type) => (
                <Select.Option key={type.id} value={type.id} initialValues={true}>
                  {type.type}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name='volumeOfPotentialId' label='Monthly Selling Volume'>
            <Select>
              {volumeOfPotential?.map((volume) => (
                <Select.Option key={volume.id} value={volume.id} initialValues={true}>
                  {volume.volume}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Divider orientation='left' orientationMargin='0'>
        Bank Information
      </Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={['bankInformation', 'accountNumber']}
            label='Account Number'
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder='Account Number' />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={['bankInformation', 'bankName']}
            label='Bank Name'
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder='Bank Name' />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={['bankInformation', 'bankBranch']}
            label='Bank Branch'
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder='Bank Branch' />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default EditSellerForm;
